import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, MapPin } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { TrustStrip } from "@/components/global/TrustStrip";
import { HeroSection } from "@/components/HeroSection";
import { WhyChooseSection } from "@/components/WhyChooseSection";
import { ServicesSection } from "@/components/ServicesSection";
import { Footer } from "@/components/Footer";
import { StickyCallBar } from "@/components/StickyCallBar";
import { useSEO } from "@/hooks/useSEO";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { EmergencyChecklistSection } from "@/components/EmergencyChecklistSection";
import { CaseHighlightsSection } from "@/components/CaseHighlightsSection";
import { buildReviewSchema } from "@/data/testimonials";
import { homeFaqs } from "@/data/homeFaqs";

// Lazy-load below-the-fold sections to reduce initial JS bundle
const MeetBenSection = lazy(() => import("@/components/MeetBenSection").then(m => ({ default: m.MeetBenSection })));
const CredentialsStrip = lazy(() => import("@/components/CredentialsStrip").then(m => ({ default: m.CredentialsStrip })));
const ServiceAreaSection = lazy(() => import("@/components/ServiceAreaSection").then(m => ({ default: m.ServiceAreaSection })));
const CTASection = lazy(() => import("@/components/CTASection").then(m => ({ default: m.CTASection })));
const WaterCategoriesSection = lazy(() => import("@/components/WaterCategoriesSection").then(m => ({ default: m.WaterCategoriesSection })));
const ProcessSection = lazy(() => import("@/components/ProcessSection").then(m => ({ default: m.ProcessSection })));
const FAQSection = lazy(() => import("@/components/FAQSection").then(m => ({ default: m.FAQSection })));

const LiveChat = lazy(() => import("@/components/global/LiveChat").then(m => ({ default: m.LiveChat })));

const Index = () => {
  useSEO({
    title: "Water Damage Restoration New Braunfels TX | WTR",
    description: "24/7 water damage cleanup and restoration in New Braunfels TX. Fast response, IICRC certified, insured and local. Call 830-360-2600 now.",
    keywords: "water damage restoration New Braunfels TX, emergency water extraction, flood cleanup, mold remediation, IICRC certified, licensed adjuster, Ben Jernigan, Central Texas",
    openGraph: {
      title: "Water Damage Restoration New Braunfels TX | WTR",
      description: "24/7 water damage cleanup and restoration in New Braunfels TX. Fast response, IICRC certified, insured and local. Call 830-360-2600 now.",
      type: "website"
    },
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "mainEntity": homeFaqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer },
          })),
        },
        buildReviewSchema(),
      ],
    },
  });

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <header>
        <Navigation />
      </header>
      <TrustStrip />
      
      <main>
        <HeroSection />

        {/* Fast paths: emergency + city pages (strong internal links for crawl priority) */}
        <section className="py-10 bg-background border-b border-border">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-xl font-bold text-wtr-gray mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  Water in Your Home Right Now?
                </h2>
                <ul className="space-y-2.5">
                  <li>
                    <Link to="/emergency" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      24/7 emergency response — what to do first <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/emergency-water-damage" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      Emergency water damage cleanup <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/water-extraction" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      Emergency water extraction and drying <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/60-minute-water-damage-survival-guide" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      The 60-minute water damage survival guide <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-xl font-bold text-wtr-gray mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-wtr-blue" />
                  Local Crews Near You
                </h2>
                <ul className="space-y-2.5">
                  <li>
                    <Link to="/new-braunfels" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      Water damage restoration in New Braunfels <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/seguin" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      Water damage restoration in Seguin <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/san-marcos" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      Water damage restoration in San Marcos <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                  <li>
                    <Link to="/canyon-lake" className="inline-flex items-center gap-1.5 text-wtr-blue font-semibold hover:underline">
                      Water damage restoration in Canyon Lake <ArrowRight className="w-4 h-4" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        
        
        <section>
          <TestimonialsSection />
        </section>
        
        <section>
          <EmergencyChecklistSection />
        </section>
        
        <section>
          <WhyChooseSection />
        </section>
        
        <section>
          <ServicesSection />
        </section>
        
        <Suspense fallback={null}>
          <section>
            <MeetBenSection />
          </section>
          
          <section>
            <CredentialsStrip />
          </section>
          
          <section>
            <ServiceAreaSection />
          </section>
          
          <CTASection />
          
          <section>
            <WaterCategoriesSection />
          </section>
          
          
          <section>
            <ProcessSection />
          </section>
          
          <section>
            <CaseHighlightsSection />
          </section>
          
          <section>
            <FAQSection />
          </section>
        </Suspense>
      </main>
      
      <Footer />
      <StickyCallBar />
      <Suspense fallback={null}>
        <LiveChat />
      </Suspense>
    </div>
  );
};

export default Index;
